import { Store } from "shared/src/stores/Store";

import { SubstitutesClient } from "../../clients/substitutes/SubstitutesClient";
import { Params } from "../../clients/substitutes/SubstitutesClient";
import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";
import { RequestStateContainer } from "../utils/RequestStateContainer";
import { LoadState } from "../utils/RequestStateContainer";
import TrackingUtil from "shared/src/utils/TrackingUtil";
import { BettyBundleId, Bundle, BundleId, BundleStoreData } from "shared/src/types/articlesearch/Article";
import { SearchResultRenderData } from "shared/src/types/RenderData";
type RequestParams = Params;

export type BundleSelectorByNumber = {
    [x: string]: string;
};
export type BundleSelectorById = {
    [x: string]: string;
};

function findBundleByNumber(
    bundleNumber: string,
    bundleDataById: {
        [x: string]: Bundle;
    }
): Bundle | undefined | null {
    for (const bundleId in bundleDataById) {
        const bundle = bundleDataById[bundleId];
        if (bundle && bundle.bettyBundleId.bundleNumber === bundleNumber) {
            return bundle;
        }
    }
    return undefined;
}

export function bundleNumberMapToBundleIdMap(
    bundlesByNumber: BundleSelectorByNumber,
    bundleDataById: {
        [x: string]: Bundle;
    }
): BundleSelectorById {
    const result: BundleSelectorById = {};
    for (const bundleNumber in bundlesByNumber) {
        const selected = bundlesByNumber[bundleNumber];
        const bundle: Bundle | undefined | null = findBundleByNumber(bundleNumber, bundleDataById);
        if (bundle) {
            result[bundle.bettyBundleId.bettyBundleId] = selected;
        }
    }
    return result;
}

/**
 * Retrieves Dana Recommendations from Evaluate and transforms them into something renderable.
 */
export class SubstitutesStoreImpl extends Store {
    container!: RequestStateContainer<Array<SearchResultRenderData>, RequestParams>; // initialized in resetState

    constructor() {
        super();
        this.resetState();
    }

    transformBundle = (article: any, b: any): Bundle => {
        const storeId = article.storeId;
        const selectedDeliveryMode = b.deliveryMode;
        const selectedFulfillmentType = b.fulfillmentType;
        const fulfillmentObject: {
            [x: string]: {
                [x: string]: any;
            };
        } = {};
        fulfillmentObject[selectedFulfillmentType] = {
            availability: b.availability
        };
        const availabilityData: {
            [x: string]: {
                [x: string]: any;
            };
        } = {};
        availabilityData[selectedDeliveryMode] = {
            possibleFulfillmentTypes: fulfillmentObject
        };
        const stores: { [k: string]: BundleStoreData } = {};
        const storeData: BundleStoreData = {
            customerBuyable: b.customerBuyable,
            employeeBuyable: b.employeeBuyable,
            possibleDeliveryModes: availabilityData,
            sellingPriceInfo: b.priceInfo,
            delisted: false,
            selectedDeliveryMode,
            selectedFulfillmentType,
            selectedFTEvaluatePromotions: b.selectedFTEvaluatePromotions
        };
        stores[storeId] = storeData;
        const bundleId: BundleId = {
            bettyBundleId: b.bundleId
        };
        const bettyBundleId: BettyBundleId = {
            bundleNumber: b.bundleNumber,
            bettyBundleId: b.bundleId,
            bettyVariantId: article.variantId,
            variantNumber: article.variantNumber,
            articleNumber: undefined
        };
        const result: Bundle = {
            bundleId,
            bettyBundleId,
            categories: b?.categories,
            customerDisplayId: b.customerDisplayId,
            content: b.content,
            description: b.title,
            brandName: b.brandName,
            imageUrl: b.imageUrl,
            fallbackImageUrl: article.fallbackImageUrl,
            availability: b.availability,
            customerAvailability: b.availability,
            employeeAvailability: b.availability,
            showCustomerAvailability: true, // ??
            showEmployeeAvailability: true, // ??
            isWeightArticle: b.weightArticle,
            weightPerPiece: undefined,
            offers: [],
            trackingInfo: {
                trackingId: b.trackingInfo
            },
            stores: stores,
            ownBrand: b.ownBrand,
            recentlyAddedExpiration: b.recentlyAddedExpiration,
            refBundleIds: b.refBundleIds
        };
        return result;
    };

    resetState() {
        this.container = new RequestStateContainer({
            request: (params: RequestParams) => {
                return SubstitutesClient.getSubstitutes(params);
            },
            onSuccess: () => {
                this.trigger();
            },
            onError: (error) => {
                TrackingUtil.trackUiErrorCa("SubstitutesStore", "loading dana recommendation failed", error);
                this.trigger();
            },
            transformResponse: (response: Array<any>): Array<SearchResultRenderData> => {
                return (response ?? []).slice(0, 3).map((r: any): SearchResultRenderData => {
                    const b = r.bundles[0];
                    const bundlesTransformed = (r.bundles ?? []).map((b: Bundle) => this.transformBundle(r, b));
                    const bundlesAsMap = bundlesTransformed.reduce((acc: { [key: string]: Bundle }, o: Bundle) => {
                        acc[o.bundleId.bettyBundleId] = o;
                        return acc;
                    }, {} as { [key: string]: Bundle });
                    return {
                        name: b.title,
                        variant: r,
                        variantId: {
                            articleNumber: r.articleId,
                            bettyVariantId: r.variantId,
                            variantNumber: r.variantNumber
                        },
                        position: 0,
                        type: "SearchResult",
                        url: b.imageUrl || r.fallbackImageUrl,
                        bundleSelector: bundleNumberMapToBundleIdMap(r.bundleSelector, bundlesAsMap),
                        selectedBundle: b.bundleId,
                        bundles: bundlesAsMap,
                        debugInfo: undefined,
                        ltrDebugInfo: null
                    };
                });
            }
        });
    }

    update(articleId: string): Promise<any> | undefined | null {
        const countryCode = SessionStore.getCountry() ? SessionStore.getCountry().toLowerCase() : "de";
        return this.container.load({
            countryCode: countryCode,
            locale: SessionStore.getLocale(),
            articleId,
            storeId: SessionStore.getCustomerStore(),
            customerId: SessionStore.getCustomerId(),
            isCustomer: SessionStore.isCustomer()
        });
    }

    getData(): Array<SearchResultRenderData> {
        return this.container.getState().data || [];
    }

    getLoadState(): LoadState {
        return this.container.getState().loadState;
    }
}
export const SubstitutesStore = new SubstitutesStoreImpl();
